// frontend/src/utils/dirtyCellPayload.js
//
// The bulk-save payload of an editable grid, built from the cells the merchant
// actually changed: `{ items: [{ id, fields }] }` carrying ONLY the edited
// fields of each row.
//
// Never the whole row. A bulk-update endpoint reads a field's presence as an
// instruction: a safety stock that arrives is an override
// (backend/api/services/safety_stock.py), an on-hand that arrives is pushed to
// the sales channel (backend/api/views/inventory.py), a supplier-catalog field
// that arrives is checked against the row's ERP gate. A row sent whole turns
// every cell the merchant never touched into one of those — and writes back
// the value the grid loaded, over whatever changed on the server since.

/**
 * @param {Map<string, {rowId: any, fieldName: string, rowData: object}>} changedCells
 *   useEditableGrid's dirty-cell map (`getChangedCellsRef()`), one entry per
 *   edited cell.
 * @param {Object<string, string>} [wireFields]  Column field → the row key the
 *   server writes it through, for a column whose `field` is a read-only
 *   rendering — a referencePickerColDef picker keeps the nested option on
 *   `row[field]` and the id it writes on `row[`${field}_id`]`. The value is
 *   read from, and sent under, the wire key.
 * @returns {{items: Array<{id: any, fields: object}>}}  One item per edited
 *   row, in first-edit order; each field holds the row's CURRENT value, so a
 *   cell edited twice sends what it reads now.
 */
export function dirtyCellPayload(changedCells, wireFields = {}) {
  const byRow = new Map();
  for (const { rowId, fieldName, rowData } of changedCells.values()) {
    if (!byRow.has(rowId)) byRow.set(rowId, {});
    const wireField = wireFields[fieldName] ?? fieldName;
    // JSON drops an undefined field, so the save would answer success having
    // written nothing. A cleared cell holds null.
    if (rowData[wireField] === undefined) {
      throw new Error(`dirtyCellPayload: edited cell ${fieldName} of row ${rowId} has no ${wireField} value`);
    }
    byRow.get(rowId)[wireField] = rowData[wireField];
  }
  return {
    items: [...byRow.entries()].map(([id, fields]) => ({ id, fields })),
  };
}
